import classNames from 'classnames';
import { observer } from 'mobx-react';
import * as React from 'react';
import { action, observable, makeObservable } from 'mobx';
import { NewModal } from 'Views/Components/Modal/NewModal';
import Modal, { IModalProps } from './Modal';

type globalModalState = IModalProps & { children: React.ReactNode };

export interface IGlobalModal {
	/**
	 * Shows a modal on the screen
	 * @param label A label for the modal for screen readers
	 * @param children The content to display inside the modal
	 * @param modalProps
	 */
	show: (label: string, children: React.ReactNode, modalProps?: Partial<IModalProps>) => void;
	/**
	 * Hides the global dialog
	 */
	hide: () => void;
}

/**
 * A global modal that can be called imperatively from the store
 * This component should only be constructed by the top level App component
 */
class GlobalModal extends React.Component implements IGlobalModal {
	/** Defaults for the modal state */
	private get defaultModalState(): globalModalState {
		return {
			label: '',
			children: null,
			isOpen: false,
			className: undefined,
			onRequestClose: this.hide,
		};
	}

	/**
	 * The props of the modal controlled by this component
	 */
	@observable
	private modalState: globalModalState = this.defaultModalState;

	constructor(props: Record<string, never>) {
		super(props);
		makeObservable(this);
	}

	/** @inheritDoc */
	@action
	public show = (label: string, children: React.ReactNode, modalProps: Partial<IModalProps> = {}) => {
		// Mutate individual properties to maintain observable reactivity
		// Replacing the entire object would break MobX tracking
		const defaults = this.defaultModalState;
		this.modalState.isOpen = true;
		this.modalState.children = children;
		this.modalState.label = label;
		this.modalState.className = modalProps.className ?? defaults.className;
		this.modalState.onRequestClose = modalProps.onRequestClose ?? defaults.onRequestClose;

		// Merge any additional modal props
		Object.assign(this.modalState, modalProps);
	};

	/** @inheritDoc */
	@action
	public hide = () => {
		this.modalState.isOpen = false;
	};

	public render() {
		const { newModal = false } = this.modalState;

		if (newModal) {
			return (
				<NewModal
					{...this.modalState}
					overlayClassName={classNames('new-modal-container--global', this.modalState.overlayClassName)}
				>
					{this.modalState.children}
				</NewModal>
			);
		}

		return <Modal {...this.modalState}>{this.modalState.children}</Modal>;
	}
}

export default observer(GlobalModal);
